import { CheckCircle2, ChevronLeft, ExternalLink, LifeBuoy, RotateCcw, Trash2 } from "lucide-react";
import { Link } from "react-router";

import { Button } from "@runcheetah/ui";
import { StatusBadge } from "@/pages/brands";

import type { BrandDetail, BrandStatus } from "./types";

export function BrandDetailHeader({
  brand,
  healResult,
  isHealing,
  isDeleting,
  isResolvingSupportRequest,
  onHealOnboarding,
  onResolveSupportRequest,
  onDelete,
}: {
  brand: BrandDetail;
  healResult: string | null;
  isHealing: boolean;
  isDeleting: boolean;
  isResolvingSupportRequest: boolean;
  onHealOnboarding: () => void;
  onResolveSupportRequest: () => void;
  onDelete: () => void;
}) {
  const workerRunsPath = brand.supportRequest?.workerJobId
    ? `/runs?search=${encodeURIComponent(brand.supportRequest.workerJobId)}`
    : null;

  return (
    <div className="overflow-hidden rounded-3xl border bg-card shadow-xs">
      <div className="flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 flex-1 items-start gap-4">
          <Button variant="ghost" size="icon" className="mt-1 shrink-0 rounded-full" asChild>
            <Link to="/brands" aria-label="Back to brands">
              <ChevronLeft className="size-4" />
            </Link>
          </Button>
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="max-w-full truncate text-3xl font-semibold tracking-[-0.03em]">
                {brand.sourceDomain}
              </h2>
              <StatusBadge status={brand.status as BrandStatus} />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span className="max-w-full truncate font-mono">{brand.id}</span>
              <span className="hidden h-1 w-1 rounded-full bg-muted-foreground/40 sm:inline-block" />
              <a
                href={brand.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex max-w-full items-center gap-1 truncate hover:text-foreground"
              >
                <span className="truncate">{brand.sourceUrl}</span>
                <ExternalLink className="size-3.5 shrink-0" />
              </a>
            </div>
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2 lg:justify-end">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/brands/${brand.id}/campaigns`}>Campaign Boards</Link>
          </Button>
          <Button variant="secondary" size="sm" asChild>
            <Link to={`/social-post-planner?brandId=${brand.id}`}>Social Planner</Link>
          </Button>
          {brand.status === "failed" ? (
            <Button variant="outline" size="sm" onClick={onHealOnboarding} disabled={isHealing}>
              <RotateCcw className="mr-2 size-4" />
              {isHealing ? "Healing..." : "Heal onboarding"}
            </Button>
          ) : null}
          <div className="h-8 w-px bg-border" />
          <Button
            variant="outline"
            size="sm"
            className="border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground"
            onClick={onDelete}
            disabled={isDeleting}
          >
            <Trash2 className="mr-2 size-4" />
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </div>
      {brand.supportRequest ? (
        <div className="border-t border-amber-200/60 bg-amber-50 px-5 py-4 text-sm text-amber-950 dark:border-amber-500/30 dark:bg-amber-950/25 dark:text-amber-100">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <LifeBuoy className="mt-0.5 size-4 shrink-0" />
              <div className="min-w-0 space-y-1">
                <p className="font-medium">Support requested for this onboarding</p>
                <p className="text-amber-900/75 dark:text-amber-100/75">
                  Requested {new Date(brand.supportRequest.createdAt).toLocaleString()}.
                  {brand.supportRequest.workerJobId
                    ? ` Linked worker job ${brand.supportRequest.workerJobId}.`
                    : " No failed worker job was linked."}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              {workerRunsPath ? (
                <Button variant="outline" size="sm" asChild>
                  <Link to={workerRunsPath}>View worker runs</Link>
                </Button>
              ) : null}
              <Button
                variant="secondary"
                size="sm"
                onClick={onResolveSupportRequest}
                disabled={isResolvingSupportRequest}
              >
                <CheckCircle2 className="mr-2 size-4" />
                {isResolvingSupportRequest ? "Resolving..." : "Mark resolved"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
      <div className="border-t bg-muted/20 px-5 py-3 text-sm text-muted-foreground">
        <span>
          Social Planner is an admin tool for custom one-off generation outside campaign boards.
        </span>
        {healResult ? <span className="ml-2 text-foreground">{healResult}</span> : null}
      </div>
    </div>
  );
}
